import 'styles/app.scss';

import 'isomorphic-fetch';
import * as React from 'react';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { AppShell } from 'app/shell/app-shell';
import { safely } from 'app/infrastructure/universal/execution';
import { enableStartupCrashNotifications } from 'app/infrastructure/notifications';
import { configureEnvironment } from 'app/startup/runtime-configuration';
import { LangDir } from 'app/components/lang-dir';
import { routes } from 'dependencies/routes';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import { DndProvider } from 'react-dnd-multi-backend';
import { createBrowserHistory, History } from 'history';
import { getHostingPath } from 'app/infrastructure/hosting';
import { createRoot } from 'react-dom/client';
import { setGlobalDevModeChecks } from 'reselect';
import { getReselectCheckOptions } from 'app/infrastructure/configure-store';
import { createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom';

/**
 * Setup client-side rendering
 * Get the application-wide store instance.
 */
const installGlobalErrorHandler = enableStartupCrashNotifications();

const baseUrl = getHostingPath();

export const history: History = createBrowserHistory();

/**
 * Renders the application
 */
async function renderFromRoot() {

  const { store } = configureEnvironment(history);

  setGlobalDevModeChecks(getReselectCheckOptions());

  const shell = new AppShell();

  await shell.initialize();

  const container = document.getElementById('react-app');
  const root = createRoot(container);

  const router = createBrowserRouter(
    createRoutesFromElements(routes),
    {
      basename: baseUrl
    }
  );

  root.render(
    <>
      <LangDir locale={AppShell.locale} />
      <DndProvider options={HTML5toTouch}>
        <Provider store={store}>
          <IntlProvider locale={AppShell.locale}>
            <RouterProvider router={router} />
          </IntlProvider>
        </Provider>
      </DndProvider>
    </>
  );
}

safely(renderFromRoot).then(installGlobalErrorHandler).then(() =>

  console.log(`


.............................................................................
.............................................................................
.............................................................................
.............................................................................
.............................................................................
.............................................................................
.............................................................................


;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

"""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""
"""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""""



                                                     \`,,,.    ,    ,  ,    ..
                                                     :+;;;#"  ##   #\` +"   # 
                                                     :;    #  #.#  #\`  #  "" 
                                                     :;    #  # \`# #\`  ;+ # 
                                                     :;   \`#  #   ##\`   #+: 
                                                     :#####   #    #\`   ,#   



 ............................................................................



███████╗██╗   ██╗███╗   ██╗███████╗██████╗  ██████╗ ██╗    ██╗     ██╗███████╗███████╗
██╔════╝╚██╗ ██╔╝████╗  ██║██╔════╝██╔══██╗██╔════╝ ██║    ██║     ██║██╔════╝██╔════╝
███████╗ ╚████╔╝ ██╔██╗ ██║█████╗  ██████╔╝██║  ███╗██║    ██║     ██║█████╗  █████╗
╚════██║  ╚██╔╝  ██║╚██╗██║██╔══╝  ██╔══██╗██║   ██║██║    ██║     ██║██╔══╝  ██╔══╝
███████║   ██║   ██║ ╚████║███████╗██║  ██║╚██████╔╝██║    ███████╗██║██║     ███████╗
╚══════╝   ╚═╝   ╚═╝  ╚═══╝╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝    ╚══════╝╚═╝╚═╝     ╚══════╝



 `)).catch((e) => console.log(`Synergi Life failed to start: ${e}`));
